import React, { useEffect, useId, useMemo, useRef, useState } from "react";
import {
  useConsultOrderContext,
  ConsultOrderAddressMode,
  ConsultOrderFulfillment,
  DeliveryServiceFee,
} from "../../contexts/ConsultOrderContext";
import {
  BranchServiceItem,
  CncOrderDeliveryType,
  DeliveryTimeSlotsResponse,
  PersonalIdType,
} from "../../types/consultOrder.types";
import {
  ConsultOrderValidationErrors,
  PHARMACY_REQUIRED_ERROR,
  deliveryFeeKey,
  deliveryZoneKey,
} from "../../utils/consultOrderPayload";
import { resolveServiceFee } from "../../utils/consultServiceFee";
import {
  checkDeliveryDistance,
  checkDeliveryTimeSlots,
  getBranchServiceItems,
} from "../../services/consultOrderService";
import { CONSULT_LOYALTY_PAYMENT_ENABLED } from "../../utils/consultOrder.constants";
import AddressCreate from "./AddressCreate";
import styles from "./DeliverySection.module.scss";

// ITAM-2583: the pickup/delivery half of the consult-order form, ported for
// the ITAM-2887 advisor portal. Fully context-driven (reads/writes the shared
// draft); the only prop is the field-linked validation error map.
//
// Advisor-portal differences from the pharmacist build:
//  - the pharmacy is the draft's own pick (`draft.pharmacy`): its pair keys the
//    SERVICE-item fetch, its detail coordinates feed the zone and time-slot
//    pre-checks, and both recorded results carry the pair in their key, so a
//    pharmacy change can never reuse an approval or a fee of the previous one;
//  - with no pharmacy picked the fulfillment choice is disabled and nothing is
//    fetched; while the pharmacy detail is loading the zone check waits;
//  - the "Nova adresa" Places search is biased to the picked pharmacy;
//  - the caller's consent to forward their data is a required, UI-only tick.
// The pre-checks go through the shared axios instance; a 403 (the endpoints
// admit the advisor only through APOTEKA_USER) renders a notice and lets the
// backend decide.

export const FULFILLMENT_OPTIONS: {
  value: ConsultOrderFulfillment;
  label: string;
}[] = [
  { value: "PICKUP", label: "Lično preuzimanje" },
  { value: "STANDARD_DELIVERY", label: "Pomoć pri preuzimanju" },
  { value: "FAST_DELIVERY", label: "Brza pomoć pri preuzimanju" },
];

const PERSONAL_ID_TYPE_OPTIONS: { value: PersonalIdType; label: string }[] = [
  { value: "JMBG", label: "JMBG" },
  { value: "PERSONAL_CARD_NUMBER", label: "Broj lične karte" },
  { value: "PASSPORT_NUMBER", label: "Broj pasoša" },
];

const slotOptionValue = (start: string, end: string) => `${start}|${end}`;

/** The branch SERVICE items of one pharmacy pair (the fee source). */
interface ServiceItemsState {
  pairKey: string;
  status: "loading" | "loaded" | "error";
  items: BranchServiceItem[];
}

type CheckError = "role" | "missing-marketplace";

interface DeliverySectionProps {
  errors?: ConsultOrderValidationErrors;
}

const DeliverySection: React.FC<DeliverySectionProps> = ({ errors }) => {
  const { draft, updateDraft, prefill } = useConsultOrderContext();

  const reactId = useId();
  const consentId = `${reactId}-delivery-consent`;
  const consentErrId = `${consentId}-error`;
  const personalIdErrId = `${reactId}-personal-id-error`;

  const pharmacy = draft.pharmacy;
  const hasPharmacy = pharmacy != null;
  const isDelivery = draft.fulfillment !== "PICKUP";
  // The delivery UI (and every fetch behind it) needs a picked pharmacy.
  const deliveryActive = isDelivery && hasPharmacy;

  const marketplaceId = pharmacy?.marketplaceId ?? null;
  const branchMarketplaceId = pharmacy?.branchMarketplaceId ?? null;
  const pairKey =
    marketplaceId != null && branchMarketplaceId != null
      ? `${marketplaceId}:${branchMarketplaceId}`
      : null;
  const detailStatus = pharmacy?.detailStatus ?? null;
  const marketplaceLat =
    detailStatus === "loaded" ? pharmacy?.latitude ?? null : null;
  const marketplaceLng =
    detailStatus === "loaded" ? pharmacy?.longitude ?? null : null;

  // Branch SERVICE items = the delivery-fee source (the nullable marketplace
  // deliveryPrice* is NOT trusted). Fetched per pharmacy pair while the
  // delivery UI is active; the state is tagged with the pair it belongs to so
  // a previous pharmacy's items are never read for the current one.
  const [serviceItems, setServiceItems] = useState<ServiceItemsState | null>(
    null
  );

  useEffect(() => {
    if (!deliveryActive || marketplaceId == null || branchMarketplaceId == null) {
      return;
    }
    const key = `${marketplaceId}:${branchMarketplaceId}`;
    const controller = new AbortController();
    setServiceItems({ pairKey: key, status: "loading", items: [] });
    getBranchServiceItems(marketplaceId, branchMarketplaceId, controller.signal)
      .then((items) => {
        if (controller.signal.aborted) {
          return;
        }
        setServiceItems({ pairKey: key, status: "loaded", items });
      })
      .catch(() => {
        if (controller.signal.aborted) {
          return;
        }
        setServiceItems({ pairKey: key, status: "error", items: [] });
      });
    return () => controller.abort();
  }, [deliveryActive, marketplaceId, branchMarketplaceId]);

  // Resolve the fee for the CURRENT pharmacy and delivery type, tagged with
  // their key so validation only trusts a fee resolved for this selection.
  const serviceFee = useMemo<DeliveryServiceFee | null>(() => {
    if (!deliveryActive || pharmacy == null) {
      return null;
    }
    const key = deliveryFeeKey(pharmacy, draft.fulfillment);
    if (
      serviceItems == null ||
      serviceItems.pairKey !== pairKey ||
      serviceItems.status === "loading"
    ) {
      return { key, status: "loading", serviceItemId: null, price: null };
    }
    if (serviceItems.status === "error") {
      return { key, status: "error", serviceItemId: null, price: null };
    }
    const resolved = resolveServiceFee(
      serviceItems.items,
      draft.fulfillment as CncOrderDeliveryType
    );
    return {
      key,
      status: resolved.status,
      serviceItemId: resolved.serviceItemId,
      price: resolved.price,
    };
  }, [deliveryActive, pharmacy, pairKey, draft.fulfillment, serviceItems]);

  // Mirror the resolved fee into the shared draft so the lifecycle submit()
  // (validation + payload) can read it. Guarded to avoid a render loop: only
  // write when a field actually changed.
  useEffect(() => {
    if (!serviceFee) {
      if (draft.deliveryServiceFee) {
        updateDraft({ deliveryServiceFee: null });
      }
      return;
    }
    const cur = draft.deliveryServiceFee;
    if (
      !cur ||
      cur.key !== serviceFee.key ||
      cur.status !== serviceFee.status ||
      cur.serviceItemId !== serviceFee.serviceItemId ||
      cur.price !== serviceFee.price
    ) {
      updateDraft({ deliveryServiceFee: serviceFee });
    }
  }, [serviceFee, draft.deliveryServiceFee, updateDraft]);

  const feeResolved = serviceFee?.status === "ok";
  const fee =
    feeResolved && typeof serviceFee?.price === "number" ? serviceFee.price : 0;
  const loyalty = prefill?.user.userLoyaltyPoints ?? 0;
  // Loyalty payment is offered only while the kill switch is ON, and then only
  // once the fee is resolved AND the balance covers it; otherwise it is hidden
  // (switch off) or disabled, and the effect below forces the selection to CASH.
  const loyaltyDisabled =
    !CONSULT_LOYALTY_PAYMENT_ENABLED || !feeResolved || loyalty < fee;

  // Local pre-check state (never persisted to the draft/payload).
  const [checking, setChecking] = useState(false);
  const [zoneOk, setZoneOk] = useState<boolean | null>(null);
  const [slotsResponse, setSlotsResponse] =
    useState<DeliveryTimeSlotsResponse | null>(null);
  const [checkError, setCheckError] = useState<CheckError | null>(null);

  // Currently-resolved delivery coordinates (SAVED selected address or the
  // Places-created NEW address). null => nothing to pre-check yet.
  const resolvedCoords = useMemo<{ lat: number; lng: number } | null>(() => {
    if (draft.fulfillment === "PICKUP") {
      return null;
    }
    if (draft.addressMode === "SAVED") {
      const found = prefill?.addresses.find(
        (a) => a.id === draft.selectedAddressId
      );
      if (found && found.latitude && found.longitude) {
        return { lat: found.latitude, lng: found.longitude };
      }
      return null;
    }
    const a = draft.newAddress;
    if (a && a.latitude && a.longitude) {
      return { lat: a.latitude, lng: a.longitude };
    }
    return null;
  }, [
    draft.fulfillment,
    draft.addressMode,
    draft.selectedAddressId,
    draft.newAddress,
    prefill,
  ]);

  // The pre-checks key on these two numbers, never on the address object:
  // editing the house number, flat, floor or note of a resolved new address
  // replaces draft.newAddress on every keystroke without moving it, and must
  // neither refetch the zone and the slots nor drop the chosen slot.
  const resolvedLat = resolvedCoords?.lat ?? null;
  const resolvedLng = resolvedCoords?.lng ?? null;

  // Selected saved address lacking coordinates -> delivery is impossible.
  const savedAddressMissingCoords = useMemo(() => {
    if (draft.fulfillment === "PICKUP" || draft.addressMode !== "SAVED") {
      return false;
    }
    if (draft.selectedAddressId == null) {
      return false;
    }
    const found = prefill?.addresses.find(
      (a) => a.id === draft.selectedAddressId
    );
    return !!found && (!found.latitude || !found.longitude);
  }, [draft.fulfillment, draft.addressMode, draft.selectedAddressId, prefill]);

  // A previously-chosen standard-delivery time slot is valid only for the
  // address it was picked for (slots are fetched per delivery location).
  // Whenever the delivery ADDRESS changes, drop the stale slot: the advisor
  // must re-pick from the freshly fetched slots, and submission stays blocked
  // until they do. A pharmacy change clears the slot in the context itself.
  const addressIdentity = useMemo(() => {
    if (draft.addressMode === "SAVED") {
      return `SAVED:${draft.selectedAddressId ?? ""}`;
    }
    const a = draft.newAddress;
    return `NEW:${a ? `${a.latitude},${a.longitude}` : ""}`;
  }, [draft.addressMode, draft.selectedAddressId, draft.newAddress]);

  const prevAddressIdentityRef = useRef(addressIdentity);
  useEffect(() => {
    if (prevAddressIdentityRef.current === addressIdentity) {
      return;
    }
    prevAddressIdentityRef.current = addressIdentity;
    if (draft.deliveryTimeSlot) {
      updateDraft({ deliveryTimeSlot: null });
    }
  }, [addressIdentity, draft.deliveryTimeSlot, updateDraft]);

  // Robustness: if the latest slot options no longer include the currently
  // selected slot (e.g. a re-fetch returned a different set for the same
  // address), drop the selection so a slot that isn't actually offered can
  // never be submitted.
  useEffect(() => {
    const selected = draft.deliveryTimeSlot;
    if (!slotsResponse || !selected) {
      return;
    }
    const stillOffered = Object.values(
      slotsResponse.standardDeliveryTimeSlots
    ).some((group) =>
      group.some((s) => s.start === selected.start && s.end === selected.end)
    );
    if (!stillOffered) {
      updateDraft({ deliveryTimeSlot: null });
    }
  }, [slotsResponse, draft.deliveryTimeSlot, updateDraft]);

  // Fire the delivery pre-checks whenever the pharmacy (pair, detail state,
  // coordinates), the address coordinates or the fulfillment change, and only
  // then: the address enters as its latitude and longitude alone (see
  // resolvedLat above). Catches ALL errors (esp. a 403) so nothing throws.
  useEffect(() => {
    const resetLocal = () => {
      setZoneOk(null);
      setSlotsResponse(null);
      setCheckError(null);
    };
    if (
      draft.fulfillment === "PICKUP" ||
      marketplaceId == null ||
      branchMarketplaceId == null ||
      resolvedLat == null ||
      resolvedLng == null
    ) {
      resetLocal();
      setChecking(false);
      updateDraft({ deliveryZoneCheck: null });
      return;
    }

    // Key for THIS exact selection (pair + type + coords). Every result
    // written below is tagged with it so validation can reject a result once
    // the selection changes.
    const zoneKey = deliveryZoneKey(
      { marketplaceId, branchMarketplaceId },
      draft.fulfillment,
      resolvedLat,
      resolvedLng
    );

    if (detailStatus === "loading") {
      // The pharmacy's coordinates are still on their way: wait. Nothing is
      // recorded, so validation keeps reporting the pending check.
      resetLocal();
      setChecking(true);
      updateDraft({ deliveryZoneCheck: null });
      return;
    }

    if (marketplaceLat == null || marketplaceLng == null) {
      resetLocal();
      setChecking(false);
      setCheckError("missing-marketplace");
      // Cannot verify without the pharmacy's coordinates -> UNAVAILABLE
      // (degrade): the backend runs its own check on submit.
      updateDraft({ deliveryZoneCheck: { key: zoneKey, inZone: null } });
      return;
    }

    const controller = new AbortController();
    const deliveryType = draft.fulfillment as CncOrderDeliveryType;
    const lat = resolvedLat;
    const lng = resolvedLng;

    setChecking(true);
    setCheckError(null);
    // Drop the previous selection's slot options at the START of a new check
    // so the selector cannot offer a stale slot while this one is pending.
    setSlotsResponse(null);

    const run = async () => {
      try {
        const inZone = await checkDeliveryDistance(
          { lat, lng, marketplaceLat, marketplaceLng, deliveryType },
          controller.signal
        );
        let slots: DeliveryTimeSlotsResponse | null = null;
        if (deliveryType === "STANDARD_DELIVERY") {
          slots = await checkDeliveryTimeSlots(
            { lat, lng, marketplaceLat, marketplaceLng },
            controller.signal
          );
        }
        if (controller.signal.aborted) {
          return;
        }
        setZoneOk(inZone);
        setSlotsResponse(slots);
        setChecking(false);
        updateDraft({ deliveryZoneCheck: { key: zoneKey, inZone } });
      } catch {
        if (controller.signal.aborted) {
          return;
        }
        // A 403 (or any pre-check failure): degrade gracefully. Record
        // UNAVAILABLE (inZone null) for THIS selection so submit falls back to
        // the backend rather than hard-blocking on an unverifiable check.
        setZoneOk(null);
        setSlotsResponse(null);
        setCheckError("role");
        setChecking(false);
        updateDraft({ deliveryZoneCheck: { key: zoneKey, inZone: null } });
      }
    };

    run();
    return () => controller.abort();
  }, [
    draft.fulfillment,
    resolvedLat,
    resolvedLng,
    marketplaceId,
    branchMarketplaceId,
    detailStatus,
    marketplaceLat,
    marketplaceLng,
    updateDraft,
  ]);

  // Force payment back to CASH when loyalty is switched off outright, or when
  // the balance can no longer cover the current fee. The switch arm matters on
  // its own: a draft that already carries LOYALTY_POINTS must never reach the
  // payload with it.
  useEffect(() => {
    if (draft.paymentType !== "LOYALTY_POINTS") {
      return;
    }
    if (!CONSULT_LOYALTY_PAYMENT_ENABLED || loyalty < fee) {
      updateDraft({ paymentType: "CASH" });
    }
  }, [draft.paymentType, loyalty, fee, updateDraft]);

  const handleFulfillment = (next: ConsultOrderFulfillment) => {
    if (next === draft.fulfillment) {
      return;
    }
    if (next === "FAST_DELIVERY") {
      // No time slots for fast delivery.
      updateDraft({ fulfillment: next, deliveryTimeSlot: null });
    } else {
      updateDraft({ fulfillment: next });
    }
  };

  const handleAddressMode = (next: ConsultOrderAddressMode) => {
    if (next === draft.addressMode) {
      return;
    }
    if (next === "NEW") {
      // Switching away from SAVED clears the saved selection.
      updateDraft({ addressMode: "NEW", selectedAddressId: null });
    } else {
      // Switching away from NEW clears the Places address + save flag.
      updateDraft({
        addressMode: "SAVED",
        newAddress: null,
        saveNewAddress: false,
      });
    }
  };

  const currentSlotValue = draft.deliveryTimeSlot
    ? slotOptionValue(draft.deliveryTimeSlot.start, draft.deliveryTimeSlot.end)
    : "";

  // Live (non-submit) status for the resolved delivery fee. `ok` shows nothing
  // here (the amount is rendered in the service-fee section); every other
  // state is a reason the order cannot be priced/sent.
  const serviceFeeMessage: string | null = !serviceFee
    ? null
    : serviceFee.status === "loading"
    ? "Učitavanje usluge rezervacije…"
    : serviceFee.status === "missing"
    ? "Usluga rezervacije nije podešena za ovu radnju."
    : serviceFee.status === "duplicate"
    ? "Usluga rezervacije je dvosmislena (više stavki usluge), obratite se podršci."
    : serviceFee.status === "malformed"
    ? "Usluga rezervacije nije ispravno podešena za izabrani tip."
    : serviceFee.status === "error"
    ? "Greška pri učitavanju usluge rezervacije."
    : null;

  return (
    <div className={styles.DeliverySection}>
      {/* Fulfillment */}
      <fieldset className={styles.fieldset} disabled={!hasPharmacy}>
        <legend className={styles.legend}>Način preuzimanja</legend>
        <div className={styles.segmented} role="radiogroup">
          {FULFILLMENT_OPTIONS.map((opt) => (
            <label
              key={opt.value}
              className={styles.segment}
              data-active={draft.fulfillment === opt.value}
            >
              <input
                type="radio"
                name="consult-fulfillment"
                value={opt.value}
                checked={draft.fulfillment === opt.value}
                onChange={() => handleFulfillment(opt.value)}
              />
              <span>{opt.label}</span>
            </label>
          ))}
        </div>
        {!hasPharmacy && <p className={styles.hint}>{PHARMACY_REQUIRED_ERROR}</p>}
      </fieldset>

      {deliveryActive && (
        <>
          {/* Address mode: a saved address of the caller, or a new one. */}
          <fieldset className={styles.fieldset}>
            <legend className={styles.legend}>Adresa</legend>
            <div className={styles.segmented} role="radiogroup">
              <label
                className={styles.segment}
                data-active={draft.addressMode === "SAVED"}
              >
                <input
                  type="radio"
                  name="consult-address-mode"
                  value="SAVED"
                  checked={draft.addressMode === "SAVED"}
                  onChange={() => handleAddressMode("SAVED")}
                />
                <span>Sačuvana adresa</span>
              </label>
              <label
                className={styles.segment}
                data-active={draft.addressMode === "NEW"}
              >
                <input
                  type="radio"
                  name="consult-address-mode"
                  value="NEW"
                  checked={draft.addressMode === "NEW"}
                  onChange={() => handleAddressMode("NEW")}
                />
                <span>Nova adresa</span>
              </label>
            </div>

            {draft.addressMode === "SAVED" ? (
              <div className={styles.field}>
                <label className={styles.label} htmlFor="consult-saved-address">
                  Izaberite adresu
                </label>
                <select
                  id="consult-saved-address"
                  className={styles.select}
                  data-consult-field="address"
                  value={draft.selectedAddressId ?? ""}
                  aria-invalid={errors?.address ? true : undefined}
                  onChange={(e) =>
                    updateDraft({
                      selectedAddressId: e.target.value
                        ? Number(e.target.value)
                        : null,
                    })
                  }
                >
                  <option value="">Izaberite</option>
                  {(prefill?.addresses ?? []).map((addr) => (
                    <option key={addr.id} value={addr.id}>
                      {[addr.name, addr.number, addr.city]
                        .filter(Boolean)
                        .join(", ")}
                    </option>
                  ))}
                </select>
                {savedAddressMissingCoords && (
                  <p className={styles.warning} role="alert">
                    Izabrana adresa nema koordinate, nije moguća pomoć pri
                    preuzimanju.
                  </p>
                )}
              </div>
            ) : (
              <div
                className={styles.field}
                data-consult-field="address"
                tabIndex={-1}
              >
                <AddressCreate />
              </div>
            )}

            {errors?.address && (
              <p className={styles.error} role="alert">
                {errors.address}
              </p>
            )}
          </fieldset>

          {/* Pre-check status */}
          <div className={styles.checkStatus} aria-live="polite">
            {checking && (
              <span>Provera zone i termina pomoći pri preuzimanju…</span>
            )}
            {checkError === "missing-marketplace" && (
              <span className={styles.warning}>
                Koordinate radnje nisu dostupne, nije moguće proveriti
                zonu/termine pomoći pri preuzimanju.
              </span>
            )}
            {checkError === "role" && (
              <span className={styles.warning}>
                Nije moguće proveriti zonu/termine za ovu ulogu (potrebna backend
                dozvola).
              </span>
            )}
            {!checking && checkError == null && zoneOk === false && (
              <span className={styles.warning}>
                Adresa je van zone za pomoć pri preuzimanju za izabrani tip.
              </span>
            )}
          </div>

          {/* Delivery-fee status + focus target for a submit-time delivery error. */}
          <div
            className={styles.checkStatus}
            data-consult-field="delivery"
            tabIndex={-1}
            aria-live="polite"
          >
            {serviceFeeMessage && (
              <span
                className={
                  serviceFee?.status === "loading" ? undefined : styles.warning
                }
              >
                {serviceFeeMessage}
              </span>
            )}
            {errors?.delivery && (
              <p className={styles.error} role="alert">
                {errors.delivery}
              </p>
            )}
          </div>

          {/* Standard delivery time slots */}
          {draft.fulfillment === "STANDARD_DELIVERY" && (
            <div className={styles.field}>
              <label className={styles.label} htmlFor="consult-time-slot">
                Termin
              </label>
              <select
                id="consult-time-slot"
                className={styles.select}
                data-consult-field="deliveryTimeSlot"
                value={currentSlotValue}
                disabled={checking || !slotsResponse}
                onChange={(e) => {
                  const raw = e.target.value;
                  if (!raw) {
                    updateDraft({ deliveryTimeSlot: null });
                    return;
                  }
                  const [start, end] = raw.split("|");
                  updateDraft({ deliveryTimeSlot: { start, end } });
                }}
              >
                <option value="">Izaberite termin</option>
                {slotsResponse &&
                  Object.entries(slotsResponse.standardDeliveryTimeSlots).map(
                    ([group, slots]) => (
                      <optgroup key={group} label={group}>
                        {slots.map((slot) => (
                          <option
                            key={slotOptionValue(slot.start, slot.end)}
                            value={slotOptionValue(slot.start, slot.end)}
                          >
                            {slot.hoursSlot || `${slot.start} - ${slot.end}`}
                          </option>
                        ))}
                      </optgroup>
                    )
                  )}
              </select>
              {errors?.deliveryTimeSlot && (
                <p className={styles.error} role="alert">
                  {errors.deliveryTimeSlot}
                </p>
              )}
            </div>
          )}

          {/* Service fee, titled and priced exactly like the mobile apps:
              "Usluga rezervacije" over the amount, nothing else. */}
          <fieldset className={styles.fieldset}>
            <legend className={styles.legend}>Usluga rezervacije</legend>
            <div className={styles.radioRow}>
              <label className={styles.radio}>
                <input
                  type="radio"
                  name="consult-payment"
                  value="CASH"
                  checked={draft.paymentType === "CASH"}
                  onChange={() => updateDraft({ paymentType: "CASH" })}
                />
                <span>{feeResolved ? `${fee} RSD` : "n/a"}</span>
              </label>
              {/*
                ITAM-2756/2757: the loyalty option is hidden, so the fee is
                paid in RSD only. Kept behind the switch (not deleted) so the
                choice returns with a one-line flip, exactly as both mobile
                apps did.
              */}
              {CONSULT_LOYALTY_PAYMENT_ENABLED && (
                <label className={styles.radio} data-disabled={loyaltyDisabled}>
                  <input
                    type="radio"
                    name="consult-payment"
                    value="LOYALTY_POINTS"
                    checked={draft.paymentType === "LOYALTY_POINTS"}
                    disabled={loyaltyDisabled}
                    onChange={() =>
                      updateDraft({ paymentType: "LOYALTY_POINTS" })
                    }
                  />
                  {/* Charged in points, not dinars, hence no RSD suffix. */}
                  <span>
                    {feeResolved ? `${fee} loyalty poena` : "n/a"} ({loyalty})
                  </span>
                </label>
              )}
            </div>
          </fieldset>

          {/* Personal ID */}
          <fieldset className={styles.fieldset}>
            <legend className={styles.legend}>
              Lični identifikacioni podaci
            </legend>
            <div className={styles.fieldRow}>
              <div className={styles.field}>
                <label
                  className={styles.label}
                  htmlFor="consult-personal-id-type"
                >
                  Tip dokumenta
                </label>
                <select
                  id="consult-personal-id-type"
                  className={styles.select}
                  value={draft.personalIdType}
                  onChange={(e) =>
                    updateDraft({
                      personalIdType: e.target.value as PersonalIdType,
                    })
                  }
                >
                  {PERSONAL_ID_TYPE_OPTIONS.map((opt) => (
                    <option key={opt.value} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </select>
              </div>
              <div className={styles.field}>
                <label className={styles.label} htmlFor="consult-personal-id">
                  Broj dokumenta
                </label>
                <input
                  id="consult-personal-id"
                  className={styles.input}
                  data-consult-field="personalId"
                  type="text"
                  value={draft.personalId}
                  aria-invalid={errors?.personalId ? true : undefined}
                  aria-describedby={
                    errors?.personalId ? personalIdErrId : undefined
                  }
                  onChange={(e) => updateDraft({ personalId: e.target.value })}
                />
              </div>
            </div>
            {errors?.personalId && (
              <p id={personalIdErrId} className={styles.error} role="alert">
                {errors.personalId}
              </p>
            )}
          </fieldset>

          {/* Delivery note */}
          <div className={styles.field}>
            <label className={styles.label} htmlFor="consult-note-for-delivery">
              Napomena (opciono)
            </label>
            <textarea
              id="consult-note-for-delivery"
              className={styles.textarea}
              rows={2}
              value={draft.noteForDelivery}
              onChange={(e) => updateDraft({ noteForDelivery: e.target.value })}
            />
          </div>

          {/* The caller's consent: UI only, never sent. */}
          <div className={styles.field}>
            <div className={styles.consentRow}>
              <input
                id={consentId}
                className={styles.consentCheckbox}
                data-consult-field="deliveryConsent"
                type="checkbox"
                checked={draft.deliveryConsent}
                onChange={(e) =>
                  updateDraft({ deliveryConsent: e.target.checked })
                }
                aria-invalid={errors?.deliveryConsent ? true : undefined}
                aria-describedby={
                  errors?.deliveryConsent ? consentErrId : undefined
                }
              />
              <label className={styles.consentLabel} htmlFor={consentId}>
                Korisnik je dao saglasnost da se njegovi lični podaci i adresa
                proslede radi pomoći pri preuzimanju.
              </label>
            </div>
            {errors?.deliveryConsent && (
              <p id={consentErrId} className={styles.error} role="alert">
                {errors.deliveryConsent}
              </p>
            )}
          </div>
        </>
      )}
    </div>
  );
};

export default DeliverySection;
